import React, { useMemo, useState } from "react";
import { Button, Modal, Spinner } from "react-bootstrap";
import { addBoardMember, removeBoardMember } from "../api/boardApi";

// Surface labels — what users see. The backend keeps the technical
// owner/editor/viewer names; we map at the boundary.
const ROLE_LABEL = { owner: "Admin", editor: "Employee", viewer: "Viewer" };
const ROLE_HINT = {
  owner: "Full access — add/remove members, delete board, all content actions.",
  editor: "All content actions (groups, columns, items, cells, updates). Can't manage members or delete the board.",
  viewer: "Read-only.",
};
const ROLE_OPTIONS = ["owner", "editor", "viewer"];

/**
 * Board members tab.
 *
 * Self-contained dialog: lists every member (plus the owner separately),
 * lets admins add new members with a role, change roles inline, or remove
 * non-owner members. Viewers/employees see the same list but the controls
 * are disabled (so they know who's on the board without being able to
 * touch the roster).
 *
 * Adds an extra "All employees" picker section so admins can grant access
 * directly without leaving the dialog. The picker dedupes against current
 * members so an admin can't re-add someone who's already on.
 */
export default function MembersDialog({
  show,
  onHide,
  board,
  myRole,
  allEmployees = [],
  peopleIndex = {},
  onBoardChange,
}) {
  // Project decision: Employees can do everything Admins can, including
  // managing members. Only Viewers (and non-members) are blocked from the
  // mutation controls. Backend agrees — editor + owner share the same rank.
  const canManage = myRole === "owner" || myRole === "editor";
  const [busy, setBusy] = useState(null); // userId currently being mutated
  const [adding, setAdding] = useState({ role: "editor", q: "" });

  // Owner is implicit. Treat it as the first row, then explicit members.
  const rows = useMemo(() => {
    if (!board) return [];
    const list = [];
    if (board.owner) {
      // Avoid duplicating the owner if they also appear in `members`.
      const dupe = (board.members || []).some(
        (m) => String(m.user) === String(board.owner),
      );
      if (!dupe) list.push({ user_id: String(board.owner), role: "owner", isOwner: true });
    }
    for (const m of board.members || []) {
      list.push({
        user_id: String(m.user),
        role: m.role || "editor",
        isOwner: String(m.user) === String(board.owner),
      });
    }
    return list;
  }, [board]);

  const memberIds = new Set(rows.map((r) => r.user_id));
  const candidates = useMemo(() => {
    const q = adding.q.trim().toLowerCase();
    return allEmployees
      .filter((e) => !memberIds.has(String(e._id)))
      .filter((e) => {
        if (!q) return true;
        const name = `${e.firstname || ""} ${e.lastname || ""}`.toLowerCase();
        return name.includes(q) || (e.email || "").toLowerCase().includes(q);
      })
      .slice(0, 8);
  }, [allEmployees, memberIds, adding.q]);

  const setRole = async (userId, role) => {
    setBusy(userId);
    try {
      // addMember is upsert on the backend — same endpoint handles role changes.
      const res = await addBoardMember(board._id, { user_id: userId, role });
      onBoardChange && onBoardChange(res.data);
    } finally {
      setBusy(null);
    }
  };

  const remove = async (userId) => {
    if (!window.confirm("Remove this member from the board?")) return;
    setBusy(userId);
    try {
      await removeBoardMember(board._id, userId);
      onBoardChange &&
        onBoardChange({
          ...board,
          members: (board.members || []).filter((m) => String(m.user) !== String(userId)),
        });
    } finally {
      setBusy(null);
    }
  };

  const add = async (uid) => {
    setBusy(uid);
    setAdding((s) => ({ ...s, q: "" }));
    try {
      const res = await addBoardMember(board._id, { user_id: uid, role: adding.role });
      onBoardChange && onBoardChange(res.data);
    } finally {
      setBusy(null);
    }
  };

  const personName = (uid) => {
    const p = peopleIndex[uid];
    if (!p) return uid;
    const n = `${p.firstname || ""} ${p.lastname || ""}`.trim();
    return n || p.email || uid;
  };
  const personEmail = (uid) => peopleIndex[uid]?.email || "";

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          Members — <span className="text-muted">{board?.name}</span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {canManage && (
          <div className="members-add-row">
            <div className="members-add-head">
              <strong>Add to board</strong>
              <small className="text-muted">
                Existing members are hidden. New members are notified and granted access immediately.
              </small>
            </div>
            <div className="members-add-controls">
              <input
                type="search"
                placeholder="Search employees by name or email…"
                className="form-control form-control-sm members-search"
                value={adding.q}
                onChange={(e) => setAdding((s) => ({ ...s, q: e.target.value }))}
              />
              <select
                className="form-select form-select-sm members-role-select"
                value={adding.role}
                onChange={(e) => setAdding((s) => ({ ...s, role: e.target.value }))}
                title={ROLE_HINT[adding.role]}
              >
                {ROLE_OPTIONS.map((r) => (
                  <option key={r} value={r}>{ROLE_LABEL[r]}</option>
                ))}
              </select>
            </div>
            <small className="text-muted d-block mt-1">{ROLE_HINT[adding.role]}</small>
            {(adding.q || candidates.length > 0) && (
              <div className="members-add-results">
                {candidates.length === 0 ? (
                  <div className="members-empty">No matches</div>
                ) : (
                  candidates.map((e) => (
                    <button
                      key={e._id}
                      type="button"
                      className="members-add-item"
                      disabled={busy === e._id}
                      onClick={() => add(e._id)}
                    >
                      <span className="avatar-chip">
                        {((e.firstname?.[0] || "") + (e.lastname?.[0] || "")).toUpperCase() || "?"}
                      </span>
                      <span className="members-add-name">
                        {`${e.firstname || ""} ${e.lastname || ""}`.trim() || e.email}
                      </span>
                      <span className="members-add-email text-muted">{e.email}</span>
                      {busy === e._id ? (
                        <Spinner size="sm" animation="border" />
                      ) : (
                        <span className="members-add-cta">+ Add as {ROLE_LABEL[adding.role]}</span>
                      )}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        )}

        <div className="members-section-head">
          <strong>On this board</strong>
          <small className="text-muted">{rows.length} member{rows.length === 1 ? "" : "s"}</small>
        </div>

        <div className="members-list">
          {rows.map((r) => {
            const name = personName(r.user_id);
            const email = personEmail(r.user_id);
            const isBusy = busy === r.user_id;
            return (
              <div key={r.user_id} className="members-row">
                <span className="avatar-chip">
                  {(name || "?").slice(0, 1).toUpperCase()}
                </span>
                <div className="members-row-info">
                  <div className="members-row-name">
                    {name}
                    {r.isOwner && <span className="members-owner-tag">Owner</span>}
                  </div>
                  {email && <div className="members-row-email">{email}</div>}
                </div>
                <div className="members-row-actions">
                  {r.isOwner ? (
                    <span className="members-role-badge owner">
                      {ROLE_LABEL.owner}
                    </span>
                  ) : canManage ? (
                    <select
                      className="form-select form-select-sm"
                      value={r.role}
                      disabled={isBusy}
                      onChange={(e) => setRole(r.user_id, e.target.value)}
                      title={ROLE_HINT[r.role]}
                    >
                      {ROLE_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>{ROLE_LABEL[opt]}</option>
                      ))}
                    </select>
                  ) : (
                    <span className={`members-role-badge ${r.role}`}>{ROLE_LABEL[r.role]}</span>
                  )}
                  {canManage && !r.isOwner && (
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => remove(r.user_id)}
                      disabled={isBusy}
                    >
                      {isBusy ? <Spinner size="sm" animation="border" /> : "Remove"}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}
